import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import RoundDetailsModal from '../components/rounds/RoundDetailsModal';
import { roundsAPI, scoresAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../utils/constants';
import { getRoundLabel, canStartRound } from '../utils/roundHelpers';

export default function Events() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [selectedRoundDetails, setSelectedRoundDetails] = useState(null);
  const [selectedRoundLabel, setSelectedRoundLabel] = useState(null);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchRounds();
  }, []);

  const fetchRounds = async () => {
    try {
      setLoading(true);
      setError('');
      // Get all rounds (practice and championship)
      const data = await roundsAPI.getAll();
      setRounds(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading rounds:', err);
      setError('Failed to load events');
      setRounds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (roundId, roundLabel) => {
    try {
      setError('');
      const [roundDetails, leaderboard] = await Promise.all([
        roundsAPI.getById(roundId),
        roundsAPI.getLeaderboard(roundId)
      ]);

      setSelectedRoundId(roundId);
      setSelectedRoundDetails(roundDetails);
      setSelectedRoundLabel(roundLabel || null);
      setSelectedLeaderboard(leaderboard);
      setShowModal(true);
    } catch (err) {
      console.error('Error loading round details:', err);
      setError('Failed to load round details');
    }
  };

  const handleViewScorecard = async (roundPlayerId) => {
    try {
      const scorecard = await scoresAPI.getScorecard(roundPlayerId);
      return scorecard;
    } catch (err) {
      console.error('Error loading scorecard:', err);
      throw err;
    }
  };

  const handleStartRound = async (roundId) => {
    if (!window.confirm('Start this round? Players will be able to enter scores.')) {
      return;
    }

    try {
      await roundsAPI.start(roundId);
      fetchRounds(); // Refresh the list
    } catch (err) {
      console.error('Failed to start round:', err);
      setError(err.response?.data?.message || 'Failed to start round: ' + err.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCompleteRound = async (roundId) => {
    if (!window.confirm('Complete this round? This will finalize all scores and calculate final positions. This cannot be undone.')) {
      return;
    }

    try {
      await roundsAPI.complete(roundId);
      fetchRounds(); // Refresh the list
    } catch (err) {
      console.error('Failed to complete round:', err);
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDeleteRound = async (roundId, roundLabel) => {
    if (!window.confirm(`Delete "${roundLabel}"?\n\nThis will permanently remove all scores and records. This cannot be undone.`)) return;
    try {
      await roundsAPI.delete(roundId);
      fetchRounds();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete round');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRoundId(null);
    setSelectedRoundDetails(null);
    setSelectedLeaderboard(null);
  };

  // Group rounds by event_name
  const groupEventData = (roundsList) => {
    const eventMap = new Map();
    const standaloneRounds = [];

    roundsList.forEach(round => {
      if (round.event_name) {
        if (!eventMap.has(round.event_name)) {
          eventMap.set(round.event_name, {
            eventName: round.event_name,
            courseName: round.course_name,
            courseLocation: round.course_location,
            rounds: []
          });
        }
        eventMap.get(round.event_name).rounds.push(round);
      } else {
        standaloneRounds.push(round);
      }
    });

    // Sort rounds within each event
    eventMap.forEach(event => {
      event.rounds.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    });

    return { eventMap, standaloneRounds };
  };

  // Group all rounds first
  const allGrouped = groupEventData(rounds);

  // Separate events: if an event has ANY non-completed rounds, it's active
  const activeEvents = { eventMap: new Map(), standaloneRounds: [] };
  const completedEvents = { eventMap: new Map(), standaloneRounds: [] };

  allGrouped.eventMap.forEach((event, eventName) => {
    const hasActiveRounds = event.rounds.some(r =>
      r.status === 'not_started' || r.status === 'in_progress' || r.status === 'active'
    );

    if (hasActiveRounds) {
      activeEvents.eventMap.set(eventName, event);
    } else {
      completedEvents.eventMap.set(eventName, event);
    }
  });

  // Sort completed events by most recent first
  const sortedCompletedEvents = Array.from(completedEvents.eventMap.entries())
    .sort((a, b) => {
      const dateA = new Date(a[1].rounds[0].start_date);
      const dateB = new Date(b[1].rounds[0].start_date);
      return dateB - dateA;
    });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div>
        <div className="px-4">
          <Card>
            <p className="text-lg text-error">
              You must be logged in as admin to access event management.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {error && (
        <div className="bg-red-600 text-white px-4 py-3 mb-4 mx-4">
          {error}
        </div>
      )}

      {/* SECTION 1: Setup - Players & Courses */}
      <div className="px-4 mb-6">
        <Card>
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-gray-200 pb-2">
            Setup
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.PLAYERS)}
              className="w-full"
            >
              Manage Players
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.COURSES)}
              className="w-full"
            >
              Manage Courses
            </Button>
          </div>
        </Card>
      </div>

      {/* SECTION 2: Active Events */}
      <div className="px-4 mb-6">
        <Card>
          <div className="flex items-center justify-between mb-4 border-b-2 border-gray-200 pb-2">
            <h2 className="text-xl font-bold text-gray-900">
              New & Live Events
            </h2>
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.CREATE_EVENT)}
              className="text-sm"
            >
              + New Event
            </Button>
          </div>

          {activeEvents.eventMap.size === 0 && activeEvents.standaloneRounds.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 p-8 text-center rounded-lg">
              <p className="text-lg text-gray-600">No active events</p>
              <p className="text-sm text-gray-500 mt-2">Create a new TML event to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Array.from(activeEvents.eventMap.entries()).map(([eventName, event]) => (
                <div key={eventName} className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4">
                  {/* Event Header */}
                  <div className="border-b border-gray-300 pb-3 mb-3">
                    <h3 className="text-xl font-bold text-primary">{event.eventName}</h3>
                    <p className="text-base font-semibold text-gray-700">{event.courseName}</p>
                    <p className="text-sm text-gray-600">{event.courseLocation}</p>
                  </div>

                  {/* Event Rounds */}
                  <div className="space-y-2">
                    {event.rounds.map((round, roundIndex) => {
                      const hasPlayers = round.player_count > 0;
                      const canSetup = round.status === 'not_started' && !hasPlayers;
                      const isLive = round.status === 'in_progress' || round.status === 'active';

                      // Calculate championship round number by counting championship rounds before this one
                      let roundLabel;
                      if (round.round_type === 'practice') {
                        roundLabel = 'Practice Round';
                      } else if (round.round_type === 'championship') {
                        // Count championship rounds that come before this one (by date)
                        const championshipRoundNumber = event.rounds
                          .filter(r => r.round_type === 'championship')
                          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                          .findIndex(r => r.id === round.id) + 1;
                        roundLabel = `Championship Round ${championshipRoundNumber}`;
                      } else {
                        roundLabel = `Round ${roundIndex + 1}`;
                      }

                      const startCheck = canStartRound(event.rounds, roundIndex);

                      const isCompleted = round.status === 'completed';

                      return (
                        <div key={round.id} className={`bg-white border border-gray-200 rounded p-3 ${isCompleted ? 'opacity-60' : ''}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <span className="text-base font-bold text-gray-900">
                                {roundLabel}
                              </span>
                              {isLive && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded animate-pulse">
                                  LIVE
                                </span>
                              )}
                              {isCompleted && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                                  COMPLETED
                                </span>
                              )}
                            </div>
                            <div className="flex flex-col items-end">
                              <span className="text-xs text-gray-600">
                                {new Date(round.start_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              {hasPlayers && (
                                <span className="text-xs text-gray-500">
                                  {round.player_count} players
                                </span>
                              )}
                            </div>
                          </div>

                          {!startCheck.canStart && hasPlayers && round.status === 'not_started' && (
                            <div className="mb-2 bg-yellow-50 border border-yellow-300 rounded p-2">
                              <p className="text-xs text-yellow-800">
                                ⚠️ {startCheck.reason}
                              </p>
                            </div>
                          )}

                          <div className="flex gap-2">
                            {isCompleted ? (
                              <button
                                onClick={() => handleViewDetails(round.id, roundLabel)}
                                className="flex-1 px-3 py-2 bg-gray-100 text-gray-900 text-sm font-semibold rounded hover:bg-gray-200 transition-colors"
                              >
                                View Results
                              </button>
                            ) : canSetup ? (
                              <button
                                onClick={() => navigate(`/rounds/${round.id}/setup`)}
                                className="flex-1 px-3 py-2 bg-primary text-white text-sm font-semibold rounded hover:bg-secondary transition-colors"
                              >
                                Setup Players
                              </button>
                            ) : isLive ? (
                              <>
                                <button
                                  onClick={() => navigate(`/rounds/${round.id}`)}
                                  className="flex-1 px-3 py-2 bg-green-600 text-white text-sm font-semibold rounded hover:bg-green-700 transition-colors"
                                >
                                  View Live Round
                                </button>
                                <button
                                  onClick={() => handleCompleteRound(round.id)}
                                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded hover:bg-blue-700 transition-colors"
                                >
                                  Complete Round
                                </button>
                              </>
                            ) : startCheck.canStart ? (
                              <button
                                onClick={() => handleStartRound(round.id)}
                                className="flex-1 px-3 py-2 bg-primary text-white text-sm font-semibold rounded hover:bg-secondary transition-colors"
                              >
                                Start Round
                              </button>
                            ) : (
                              <button
                                disabled
                                className="flex-1 px-3 py-2 bg-gray-300 text-gray-500 text-sm font-semibold rounded cursor-not-allowed"
                              >
                                Cannot Start Yet
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteRound(round.id, roundLabel)}
                              className="px-3 py-2 bg-red-50 text-red-600 text-sm font-semibold rounded hover:bg-red-100 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* SECTION 3: Event History */}
      <div className="px-4 mb-6">
        <Card>
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-gray-200 pb-2">
            Event History
          </h2>

          {sortedCompletedEvents.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 p-8 text-center rounded-lg">
              <p className="text-lg text-gray-600">No completed events yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedCompletedEvents.map(([eventName, event]) => {
                const totalPoints = Math.max(...event.rounds.map(r => r.leading_points || 0));

                return (
                  <div key={eventName} className="bg-white border border-gray-300 rounded-lg p-4">
                    {/* Event Header */}
                    <div className="border-b border-gray-200 pb-3 mb-3">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-lg font-bold text-primary">{event.eventName}</h3>
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                          COMPLETED
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-700">{event.courseName}</p>
                      {totalPoints > 0 && (
                        <div className="mt-2 inline-block bg-yellow-50 border border-yellow-300 rounded px-3 py-1">
                          <span className="text-xs text-yellow-800 font-medium mr-1">Winner:</span>
                          <span className="text-base font-bold text-yellow-900">{totalPoints} pts</span>
                        </div>
                      )}
                    </div>

                    {/* Rounds */}
                    <div className="space-y-2">
                      {event.rounds.map((round, roundIndex) => (
                        <div key={round.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div>
                            <span className="text-sm font-semibold text-gray-900">Round {roundIndex + 1}</span>
                            <span className="text-xs text-gray-500 ml-2">
                              {new Date(round.start_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewDetails(round.id, `Round ${roundIndex + 1}`)}
                              className="px-3 py-1 bg-gray-100 text-gray-900 text-xs font-semibold rounded hover:bg-gray-200 transition-colors"
                            >
                              View Results
                            </button>
                            {isAuthenticated && (
                              <button
                                onClick={() => handleDeleteRound(round.id, `${eventName} - Round ${roundIndex + 1}`)}
                                className="px-3 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded hover:bg-red-100 transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Round Details Modal */}
      <RoundDetailsModal
        round={selectedRoundDetails}
        roundLabel={selectedRoundLabel}
        leaderboard={selectedLeaderboard}
        isOpen={showModal}
        onClose={handleCloseModal}
        onViewScorecard={handleViewScorecard}
      />
    </div>
  );
}
